import throttle from 'lodash/throttle';
import { Address } from './Address';

let autocompleteService: google.maps.places.AutocompleteService | null = null;
let geocoder: google.maps.Geocoder | null = null;

const internalFetchPlacePredictions = throttle<
	(
		request: google.maps.places.AutocompletionRequest,
		callback: (
			result?: google.maps.places.AutocompletePrediction[],
			status?: google.maps.places.PlacesServiceStatus
		) => void
	) => void
>((request, callback) => {
	if (autocompleteService) {
		autocompleteService.getPlacePredictions(request, callback);
	}
}, 200);

/**
 * Returns true if the Google Maps JS SDK has been loaded successfully (including the Places API) and the window.google.maps namespace is available
 */
export const googleMapsApiLoaded = (): boolean =>
	!!(
		window &&
		window.google &&
		window.google.maps &&
		window.google.maps.places
	);

/**
 * Requests a prediction from the Google Maps Places API. Throws an error if the the API hasn't been loaded beforehand
 * @param request the request
 * @param callback the function to be called upon successful retrieval of a response
 */
export const fetchPlacePredictions = (
	request: google.maps.places.AutocompletionRequest,
	callback: (
		result?: google.maps.places.AutocompletePrediction[],
		status?: google.maps.places.PlacesServiceStatus
	) => void
): void => {
	if (!googleMapsApiLoaded()) {
		throw new Error('Google Maps JS API not available.');
	}

	if (!autocompleteService) {
		autocompleteService = new google.maps.places.AutocompleteService();
	}

	internalFetchPlacePredictions(request, callback);
};

/**
 * Requests full structured address information on address string from Google Maps Geocoder API
 * @param address the address string
 */
export const geocodeByAddress = (
	address: string
): Promise<google.maps.GeocoderResult[]> => {
	if (!googleMapsApiLoaded()) {
		return Promise.reject('Google Maps JS API not available.');
	}

	if (!geocoder) {
		geocoder = new window.google.maps.Geocoder();
	}

	return new Promise((resolve, reject) => {
		if (geocoder) {
			geocoder.geocode({ address }, (results, status) => {
				if (status !== window.google.maps.GeocoderStatus.OK) {
					reject(status);
				}
				resolve(results);
			});
		} else {
			reject('Geocoder not available');
		}
	});
};

/**
 * Extracts relevant address parts (street, zip code, city) from GeocoderResult object
 * @param results the Geocoder results array
 */
export const extractAddressParts = (
	results: google.maps.GeocoderResult[]
): Address => {
	const address = {
		street: '',
		streetNumber: '',
		zipCode: '',
		city: '',
	};

	results.forEach((val) => {
		val.address_components.forEach((c) => {
			if (c.types.includes('route')) {
				address.street = c.long_name;
			} else if (c.types.includes('street_number')) {
				address.streetNumber = c.long_name;
			} else if (c.types.includes('postal_code')) {
				address.zipCode = c.long_name;
			} else if (c.types.includes('locality')) {
				address.city = c.long_name;
			}
		});
	});

	return address;
};
